import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

interface ScriptData {
  src: string;
  async: boolean;
  defer: boolean;
}

const ThirdPartyScriptLoader = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [startedLoading, setStartedLoading] = useState(false);

  const loadScript = useCallback(async ({ src, async, defer }: ScriptData) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = async;
    script.defer = defer;
    script.type = 'text/javascript';

    document.head.appendChild(script);
  }, []);
  const scriptsToLoad: ScriptData[] = [];

  if (
    process.env.KLAVIYO_COMPANY_IDENTIFIER &&
    process.env.DISABLE_KLAVIYO !== 'true'
  ) {
    scriptsToLoad.push({
      src: `https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${process.env.KLAVIYO_COMPANY_IDENTIFIER}`,
      async: true,
      defer: false,
    });
  }

  const helmetScripts: React.ReactNode[] = [];

  const googleConsentMode = () => {
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    gtag('consent', 'default', {
      ad_personalization: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'denied',
      personalization_storage: 'denied',
      security_storage: 'granted',
      wait_for_update: 500,
    });
    gtag('set', 'ads_data_redaction', true);
    gtag('set', 'url_passthrough', false);
  };

  helmetScripts.push(
    <script data-cookieconsent="ignore" id="google-consent-mode">
      {googleConsentMode()}
    </script>
  );

  // Ensure Login with Apple initializes properly on Safari
  useEffect(() => {
    const loadAppleScript = () => {
      if (document.getElementById('appleid-script')) {
        return;
      }
      const s = document.createElement('script');
      s.src =
        'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      s.async = true;
      s.id = 'appleid-script';
      s.setAttribute('data-cookieconsent', 'ignore');
      document.head.appendChild(s);
    };

    // Wait until user gives a response to Cookiebot banner to load Apple script
    // If a user already visited the site and made a choice this works as well
    if (window.Cookiebot && window.Cookiebot.consent) {
      if (window.Cookiebot.hasResponse || window.Cookiebot.consent.given) {
        loadAppleScript();
      } else {
        window.addEventListener('CookiebotOnAccept', loadAppleScript, {
          once: true,
        });
      }
    } else {
      loadAppleScript();
    }

    return () => {
      window.removeEventListener('CookiebotOnAccept', loadAppleScript);
    };
  }, []);

  if (
    process.env.NODE_ENV === 'production' &&
    process.env.DISABLE_FYLLO_PIXEL !== 'true'
  ) {
    const fylloPixel = () => {
      let ftRandom = Math.random() * 1000000;
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.visibility = 'hidden';
      iframe.style.width = '1px';
      iframe.style.height = '1px';
      iframe.src = `https://servedby.flashtalking.com/container/35891;150232;17447;iframe/?spotName=Sofar_Sounds_Spotlight_Pixel&cachebuster='+${ftRandom}+'`;
      document.body.appendChild(iframe);
    };
    helmetScripts.push(
      <script type="text/javascript" id="fyllo-pixel">
        {fylloPixel()}
      </script>
    );
  }

  useEffect(() => {
    if (!startedLoading) {
      setStartedLoading(true);
      scriptsToLoad.forEach((scriptData) => loadScript(scriptData));
    }
  }, [loadScript, startedLoading]);

  return (
    <>
      <Helmet>
        {helmetScripts.map((script, index) => (
          <React.Fragment key={`third-party-script-${index}`}>
            {script}
          </React.Fragment>
        ))}
      </Helmet>
      {children}
    </>
  );
};

export default ThirdPartyScriptLoader;
